import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import KeycloakService from 'services/KeycloakService';
import { GlobalContext } from 'providers/GlobalProvider';
import { AiWorkflowTrialUsage, CreditsAPI } from 'api/CreditsAPI';
import { getWorkflowRunId } from 'utils/workflowRunId';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;

export type WorkflowTrialBucket =
  | 'export'
  | 'import'
  | 'design'
  | 'beneficial_ownership';

// Buckets whose trial gate records the run that took the trial, under the browser's workflow
// run id. For these the status also says whether THIS run holds it, so a run that already took
// its free trial stays free after the page reloads or the workflow remounts.
const RUN_WORKFLOWS: Partial<Record<WorkflowTrialBucket, 'import_workflow' | 'design_assistant'>> = {
  import: 'import_workflow',
  design: 'design_assistant',
};

const NO_TRIAL_RUNS_USED: AiWorkflowTrialUsage = {
  export_used: false,
  import_used: false,
  design_used: false,
  beneficial_ownership_used: false,
};

/**
 * Whether the selected team can afford one run of a workflow that charges `cost`
 * credits via CreditsAPI.spendCredits, i.e. whether the paid pool covers it.
 *
 * A team a super admin flagged unlimited_workflows (credits summary) is never
 * blocked: the server skips its workflow spends entirely.
 *
 * Fails OPEN: if the balance can't be read, `insufficient` stays false so a flaky
 * summary call never locks a paying team out (the spend itself is enforced
 * server-side). `refresh` re-reads the balance, e.g. after a run is charged.
 */
export const useWorkflowCredits = (cost: number, bucket?: WorkflowTrialBucket) => {
  const { cur_user } = useContext(GlobalContext);
  const teamId = cur_user?.selected_team?.id;
  const [remaining, setRemaining] = useState<number | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  const [trialRuns, setTrialRuns] = useState<AiWorkflowTrialUsage>(NO_TRIAL_RUNS_USED);
  // Whether the current run is the team's free trial run for `bucket`: the team
  // is trialing and either hasn't used the trial (the run's first request
  // reserves it) or this run is the one holding it. A ref, read by
  // isFreeWorkflowTrial for every step of the run, so it can't go stale in a
  // completion callback's closure.
  const usesTrialRunRef = useRef(false);
  // Same value as state, so `insufficient` can stand down for the free trial run.
  const [usesTrialRun, setUsesTrialRun] = useState(false);

  const refresh = useCallback(async () => {
    if (!teamId) {
      return;
    }
    try {
      if (bucket) {
        // Same read-only check the server gate uses. Anything unverifiable counts
        // as "not a trial run", so the run is charged rather than given away.
        try {
          const runWorkflow = RUN_WORKFLOWS[bucket];
          const { data } = await axios.get(`${apiUrl}/ai-workflows/trial-status`, {
            params: {
              group_id: teamId,
              bucket: bucket,
              ...(runWorkflow ? { run_id: getWorkflowRunId(runWorkflow, teamId) } : {}),
            },
            headers: { Authorization: `Bearer ${KeycloakService.getToken()}` },
          });
          usesTrialRunRef.current = Boolean(
            data?.applicable && (!data?.used || data?.this_run),
          );
        } catch {
          usesTrialRunRef.current = false;
        }
        setUsesTrialRun(usesTrialRunRef.current);
      }
      const summary = await CreditsAPI.getCreditsSummary(teamId);
      // Any flag the summary doesn't carry counts as not used.
      setTrialRuns({
        export_used: Boolean(summary?.result?.export_used),
        import_used: Boolean(summary?.result?.import_used),
        design_used: Boolean(summary?.result?.design_used),
        beneficial_ownership_used: Boolean(
          summary?.result?.beneficial_ownership_used,
        ),
      });
      if (summary?.success && summary.result?.unlimited_workflows) {
        setUnlimited(true);
        setRemaining(null);
        return;
      }
      setUnlimited(false);
      setRemaining(
        summary?.success
          ? Number(summary.result?.remaining_credits ?? 0)
          : null,
      );
    } catch (error) {
      console.error('[credits] Failed to read credit balance', error);
      setRemaining(null);
      setTrialRuns(NO_TRIAL_RUNS_USED);
    }
  }, [teamId, bucket]);

  // True for EVERY step of the free trial run, so the caller skips
  // CreditsAPI.spendCredits for each of them. It holds until `refresh` re-reads the
  // team's trial status, which happens before the next run starts. Only safe because
  // each workflow reserves the trial with its run's first request (Import: its HTS
  // classification), which is refused for a trialing team once another run took it.
  const isFreeWorkflowTrial = useCallback((): boolean => usesTrialRunRef.current, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    remaining: remaining,
    trialRuns: trialRuns,
    isFreeWorkflowTrial: isFreeWorkflowTrial,
    // Never blocks the team's free trial run, which isn't charged.
    insufficient: !unlimited && !usesTrialRun && remaining !== null && remaining < cost,
    refresh: refresh,
  };
};
